// sales-reply-v1
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${API_BASE}/public/proposal`;
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const msg = (ex) => { const d = ex?.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : "Could not send your reply. Please try again."; };

/** Public page opened from the link in the proposal email (no login): the client accepts or asks for changes. */
export default function ProposalReply() {
  const { token } = useParams();
  const [p, setP] = useState(null);
  const [bad, setBad] = useState(false);
  const [mode, setMode] = useState("");           // "" | ACCEPT | CHANGES
  const [name, setName] = useState(""); const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [done, setDone] = useState("");
  useEffect(() => { axios.get(`${API}/${token}`).then(({ data }) => setP(data)).catch(() => setBad(true)); }, [token]);
  const send = async () => {
    setBusy(true); setErr("");
    try { const { data } = await axios.post(`${API}/${token}`, { decision: mode, name, note: note || null }); setDone(data.response); }
    catch (ex) { setErr(msg(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-secondary/40 px-4 py-10" data-testid="proposal-reply-page">
      <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-6 shadow-sm">
        {bad ? <div className="flex gap-3 text-sm"><AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" /><p>This link is not valid or has expired. Please contact the company that sent you the proposal.</p></div>
          : !p ? <p className="text-sm text-muted-foreground">Loading…</p>
          : done ? (
            <div className="space-y-3 text-center" data-testid="reply-done"><CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
              <h1 className="text-xl font-semibold">{done === "APPROVED" ? "Thank you - proposal accepted" : "Thank you - we received your changes"}</h1>
              <p className="text-sm text-muted-foreground">{p.company} has been notified{done === "APPROVED" ? " and will be in touch to take this forward." : " and will send you a revised proposal."}</p></div>
          ) : (
            <div className="space-y-4">
              <div><div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{p.company}</div>
                <h1 className="mt-1 text-xl font-semibold tracking-tight">Proposal {p.code}</h1><p className="text-sm text-muted-foreground">{p.title}</p></div>
              <div className="rounded-xl border border-border bg-secondary/40 p-4"><div className="text-xs text-muted-foreground">Quoted amount</div><div className="text-2xl font-semibold" data-testid="reply-amount">{inr(p.total_amount)}</div>
                {p.valid_until && <div className="mt-1 text-xs text-muted-foreground">Valid until {p.valid_until}</div>}</div>
              <p className="text-sm text-muted-foreground">The quotation and design drawings were attached to the email we sent you.</p>
              {!p.can_reply ? <p className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm" data-testid="reply-closed">{p.status === "ACCEPTED" ? "This proposal has already been accepted. Thank you!" : "This proposal is not open for a reply. Please contact us."}</p> : (
                <>
                  {p.response !== "PENDING" && <p className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">You replied earlier: <b>{p.response === "APPROVED" ? "accepted" : "asked for changes"}</b>. You can reply again below.</p>}
                  {!mode ? (
                    <div className="flex flex-wrap gap-3">
                      <Button className="bg-[#910029] hover:bg-[#7a0022]" onClick={() => setMode("ACCEPT")} data-testid="reply-accept">Accept proposal</Button>
                      <Button variant="outline" onClick={() => setMode("CHANGES")} data-testid="reply-changes">Request changes</Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div><Label>Your name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="reply-name" /></div>
                      <div><Label>{mode === "CHANGES" ? "What changes do you need? *" : "Any comments (optional)"}</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="reply-note" /></div>
                      {err && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{err}</p>}
                      <div className="flex gap-3">
                        <Button disabled={busy || name.trim().length < 2 || (mode === "CHANGES" && !note.trim())} onClick={send} className="bg-[#910029] hover:bg-[#7a0022]" data-testid="reply-send">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {mode === "ACCEPT" ? "Confirm acceptance" : "Send changes"}</Button>
                        <Button variant="ghost" onClick={() => { setMode(""); setErr(""); }}>Back</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
      </div>
    </div>
  );
}
